// Mirror of /app/backend/reads.py — same directives, used to render The Read
// on the site's reveal step immediately after code compile. Keep in sync.

export const SUN_READS = {
  Aries: ["Fast", "You start hot. The first hour is usually your best window. You use the spark before you overthink it."],
  Taurus: ["Anchor", "You start slow and you don't rush it. Once you're moving, you're almost impossible to stop."],
  Gemini: ["Signal", "You think out loud. Things click for you once you've said them to someone."],
  Cancer: ["Tide", "You start from how it feels. When the mood is right, the rest falls into place."],
  Leo: ["Radiance", "You set the tone in a room without trying. When you're warm, everyone around you warms up too."],
  Virgo: ["Precision", "You start by spotting what's off. You'd rather get it right than get it fast."],
  Libra: ["Balance", "You start by weighing both sides. You move once it feels fair, not before."],
  Scorpio: ["Depth", "You start all in, or not at all. Half-interested isn't really a setting you have."],
  Sagittarius: ["Range", "You start with the big picture. You need room to roam before the details matter."],
  Capricorn: ["Frame", "You start by building the plan. You like knowing the shape of things before you commit."],
  Aquarius: ["Vector", "You start from a different angle than everyone else. The obvious answer rarely holds you."],
  Pisces: ["Merge", "You start by reading the room. You feel the mood of a place before a word is said."],
};

export const MOON_READS = {
  Aries: ["Fast", "You feel it fast and let it pass fast. You'd rather deal with it now than sit in it."],
  Taurus: ["Anchor", "You need time and quiet to know what you actually feel. Don't let anyone rush your answer."],
  Gemini: ["Signal", "You process by talking it through. Saying it out loud is how you find out what you think."],
  Cancer: ["Tide", "You feel things in waves. Give it a night and it usually looks different by morning."],
  Leo: ["Radiance", "You feel steadiest when someone really sees you. One person who gets it is enough."],
  Virgo: ["Precision", "You process by sorting and fixing. Just check you're not tidying up to dodge the feeling."],
  Libra: ["Balance", "You process by talking it over with someone you trust. Left alone too long, you loop."],
  Scorpio: ["Depth", "You process privately, and all the way down. You don't share it until you've made sense of it."],
  Sagittarius: ["Range", "You process on the move. A walk clears your head faster than sitting with it."],
  Capricorn: ["Frame", "You process by turning it into a plan. Watch for the plan that quietly skips the feeling."],
  Aquarius: ["Vector", "You process by stepping back and looking from the outside. You need distance before it lands."],
  Pisces: ["Merge", "You soak up everyone else's mood as if it's yours. Working out what's actually yours is half of it."],
};

export const RISING_READS = {
  Aries: ["Fast", "You land with energy. People feel your pace before you've said much."],
  Taurus: ["Anchor", "You land steady. People relax around you because you seem hard to rattle."],
  Gemini: ["Signal", "You land curious and easy to talk to. People open up because you actually ask."],
  Cancer: ["Tide", "You land warm. People feel looked after before they know why."],
  Leo: ["Radiance", "You land noticed. The room tends to shift a little toward you."],
  Virgo: ["Precision", "You land composed. People assume you've already got it handled."],
  Libra: ["Balance", "You land calm. People feel the room you create before they even meet you."],
  Scorpio: ["Depth", "You land hard to read. The pause you hold makes people lean in."],
  Sagittarius: ["Range", "You land open and a little restless. People sense you've got somewhere to be."],
  Capricorn: ["Frame", "You land like the adult in the room. People wait for you to set the terms."],
  Aquarius: ["Vector", "You land a little different, and it works. People remember the ones who don't blend in."],
  Pisces: ["Merge", "You land soft and open. People bring their feelings to you without being asked."],
};

export const TRAIT_ADJ = {
  Fast: "Fast", Anchor: "Anchored", Signal: "Signal-tuned", Tide: "Tidal",
  Radiance: "Radiant", Precision: "Precise", Balance: "Balanced", Depth: "Deep",
  Range: "Wide-range", Frame: "Structured", Vector: "Vectored", Merge: "Merging",
};

export function buildRead({ firstName, sun, moon, rising }) {
  const [sunTrait, sunLine] = SUN_READS[sun] || ["", ""];
  const [moonTrait, moonLine] = MOON_READS[moon] || ["", ""];
  const adj = (t) => TRAIT_ADJ[t] || t;
  const stanzas = [
    { label: "Drive", trait: sunTrait, line: sunLine },
    { label: "Recovery", trait: moonTrait, line: moonLine },
  ];
  let direction;
  let composite;
  if (rising) {
    const [risingTrait, risingLine] = RISING_READS[rising] || ["", ""];
    stanzas.push({ label: "Surface", trait: risingTrait, line: risingLine });
    direction = `${sunTrait} · ${moonTrait} · ${risingTrait}`.toUpperCase();
    composite = `${adj(sunTrait)} starter. ${adj(moonTrait)} processor. ${adj(risingTrait)} surface.`;
  } else {
    direction = `${sunTrait} · ${moonTrait}`.toUpperCase();
    composite = `${adj(sunTrait)} starter. ${adj(moonTrait)} processor.`;
  }
  return { firstName, direction, stanzas, composite, timeUnknown: !rising };
}
